import { StatusProps } from 'copilot-design-system';

import { SubscriptionStatus } from 'src/constants';
import { StatusFormatter } from 'src/legacy/components/Formatters';
import { SeverityLevel } from 'src/legacy/components/UI';
import { SubscriptionEntity } from 'src/store/payments/types';
import { LineItem, Subscription } from 'src/types/subscriptions';
import { ensureUnreachable } from 'src/utils/common_utils';
import InvoiceUtils from 'src/utils/InvoiceUtils';
import { convertTitleCase } from 'src/utils/StringUtils';

export const SubscriptionInterval = {
  Day: 'day',
  Week: 'week',
  Month: 'month',
  Quarterly: 'quarterly',
  Biannually: 'biannually',
  Year: 'year',
  SemiAnnually: 'semiannually',
};
export interface StatusTagProps {
  severity: SeverityLevel;
  label: string;
  status: StatusProps['status'];
}

export default class SubscriptionUtils {
  static GetStatusTagProps = (status: string): StatusTagProps => {
    let severityLevel: SeverityLevel = SeverityLevel.low;
    let label = convertTitleCase(status);
    let statusVariant: StatusProps['status'] = 'neutral';
    const subscriptionStatus = status.toLowerCase() as SubscriptionStatus;
    switch (subscriptionStatus) {
      case SubscriptionStatus.Active:
        severityLevel = SeverityLevel.low;
        statusVariant = 'success';
        break;
      case SubscriptionStatus.Unpaid:
        severityLevel = SeverityLevel.high;
        break;
      case SubscriptionStatus.PastDue:
        severityLevel = SeverityLevel.high;
        label = 'Past due';
        statusVariant = 'warning';
        break;
      case SubscriptionStatus.Trialing:
        severityLevel = SeverityLevel.high;
        break;
      case SubscriptionStatus.Incomplete:
      case SubscriptionStatus.IncompleteExpired:
      case SubscriptionStatus.NotStarted:
        severityLevel = SeverityLevel.medium;
        label = 'Not started';
        break;
      case SubscriptionStatus.Canceled:
        severityLevel = SeverityLevel.high;
        statusVariant = 'neutral';
        break;
      default:
        ensureUnreachable(subscriptionStatus);
    }
    return { severity: severityLevel, label, status: statusVariant };
  };

  static MapCollectionMethod = (status: StatusFormatter): string => {
    switch (status.value.toLowerCase()) {
      case 'charge_automatically':
        return 'Auto';
      case 'send_invoice':
        return 'Manual';
      default:
        return '';
    }
  };

  static MapBillingPeriod = (status: StatusFormatter): string => {
    switch (status.value.toLowerCase()) {
      case 'day':
        return 'Daily';
      case 'week':
        return 'Weekly';
      case 'month':
        return 'Monthly';
      case 'quarterly':
        return 'Quarterly';
      case 'biannually':
        return 'Biannually';
      case 'year':
        return 'Yearly';
      case 'semiannually':
        return 'Every 2 years';
      default:
        return '';
    }
  };

  /**
   * Ensure that the subscription status is valid
   * @param status Subscription status
   * @returns Subscription status
   * @throws Error if status is invalid
   */
  static ensureSubscriptionStatus = (status: string): SubscriptionStatus => {
    switch (status) {
      case SubscriptionStatus.Active:
      case SubscriptionStatus.Unpaid:
      case SubscriptionStatus.PastDue:
      case SubscriptionStatus.Trialing:
      case SubscriptionStatus.Incomplete:
      case SubscriptionStatus.IncompleteExpired:
      case SubscriptionStatus.NotStarted:
      case SubscriptionStatus.Canceled:
        return status;
      default:
        throw new Error(`Invalid subscription status: ${status}`);
    }
  };

  /**
   * Determine the status of the subscription based on the fields
   * If there is a set cancelledAt property then sub is cancelled
   * otherwise use status from additionalFields
   * @param subscriptionEntity Portal subscription entity
   * @returns status of subscription
   */
  static GetSubscriptionStatus = (
    subscriptionEntity: SubscriptionEntity | Subscription,
  ): SubscriptionStatus | undefined => {
    let status = subscriptionEntity.additionalFields
      ? this.ensureSubscriptionStatus(
          subscriptionEntity.additionalFields.status || '',
        )
      : undefined;
    if (subscriptionEntity.fields.cancelledAt) {
      status = SubscriptionStatus.Canceled;
    }
    return status;
  };

  static GetTotal = (lineItems: LineItem[]): number => {
    return Number(
      InvoiceUtils.GetSumOfSubtotals(
        lineItems.map((l) => ({
          id: Number(l.id),
          description: l.description,
          quantity: Number(l.quantity),
          rate: l.rate.toString(),
          type: l.type,
          total: 'total' in l && typeof l.total === 'number' ? l.total : 0,
        })),
      ) * 100,
    );
  };
}

export const formatBillingInterval = (interval: string) => {
  if (
    interval === SubscriptionInterval.Biannually ||
    interval === SubscriptionInterval.Quarterly ||
    interval === SubscriptionInterval.SemiAnnually
  ) {
    return interval;
  }
  return `per ${interval}`;
};
